// /composables/useAlert.js
import { inject, h, ref, watch, defineComponent, markRaw } from 'vue'
import { AlertContext } from '@cnd_fe/alert'
import { MESSAGE_CONSTANTS } from '@/constants/message'

const confirmTitle = (type) => {
    switch (type) {
        case 'delete': return '삭제 확인'
        case 'insert': return '등록 확인'
        case 'update': return '수정 확인'
        case 'save': return '저장 확인'
        default: return '확인'
    }
}

const confirmContent = (type, target = '항목') => {
    switch (type) {
        case 'delete': return MESSAGE_CONSTANTS.COMMON.DELETE(`해당 ${target}`)
        case 'insert': return MESSAGE_CONSTANTS.COMMON.INSERT(target)
        case 'update': return MESSAGE_CONSTANTS.COMMON.UPDATE(target)
        case 'save': return MESSAGE_CONSTANTS.COMMON.SAVE(target)
        default: return MESSAGE_CONSTANTS.COMMON.SAVE(target)
    }
}

const successTitle = (type) => {
    switch (type) {
        case 'delete': return '삭제 완료'
        case 'insert': return '등록 완료'
        case 'update': return '수정 완료'
        case 'file-delete': return '파일 삭제 완료'
        default: return '처리 완료'
    }
}

const successMessage = (type) => {
    switch (type) {
        case 'delete': return MESSAGE_CONSTANTS.SUCCESS.DELETE
        case 'insert': return MESSAGE_CONSTANTS.SUCCESS.INSERT
        case 'update': return MESSAGE_CONSTANTS.SUCCESS.UPDATE
        case 'file-delete': return MESSAGE_CONSTANTS.SUCCESS.FILE_DELETE
        default: return MESSAGE_CONSTANTS.SUCCESS.SAVE
    }
}

export const useAlert = () => {
    const ctx = inject(AlertContext, null)
    if (!ctx) throw new Error('useAlert() must be used within <AlertProvider>.')

    // 기본 confirm
    const confirm = (type, target = '항목', options = {}) => {
        return ctx.confirm({
            type,
            variant: type === 'delete' ? 'danger' : 'primary',
            title: confirmTitle(type),
            content: confirmContent(type, target),
            confirmText: type === 'delete' ? '삭제' : '확인',
            cancelText: '취소',
            ...options
        })
    }

    // textarea 포함 confirm
    const confirmWithTextarea = async (type, target = '항목', options = {}) => {
        const { content: contentText, ...restOptions } = options

        const textareaName = restOptions.textareaName ?? 'reason'
        const placeholder = restOptions.placeholder ?? '사유를 입력하세요.'
        const required = restOptions.required ?? true
        const maxLength = restOptions.maxLength ?? 200
        const rows = restOptions.rows ?? 4
        const initialValue = restOptions.initialValue ?? ''

        const val = ref(initialValue)
        const err = ref('')

        const validate = () => {
            const v = (val.value ?? '').trim()
            if (required && !v) {
                err.value = '사유를 입력해주세요.'
                return false
            }
            if (maxLength && v.length > maxLength) {
                err.value = `사유는 ${maxLength}자 이내로 입력해주세요.`
                return false
            }
            err.value = ''
            return true
        }

        watch(val, () => {
            if (err.value) validate()
        })

        const ContentComp = markRaw(defineComponent({
            name: 'ConfirmTextareaContent',
            setup() {
                return () =>
                    h('div', { class: 'alert-textarea-wrap' }, [
                        h('p', { style: 'margin:0 0 8px 0;' }, contentText ?? confirmContent(type, target)),
                        h('textarea', {
                            id: textareaName,
                            name: textareaName,
                            rows,
                            maxlength: maxLength,
                            placeholder,
                            value: val.value,
                            style:
                                'width:100%; box-sizing:border-box; padding:8px; border:1px solid #d0d0d0; border-radius:6px; resize:vertical;',
                            onInput: (e) => (val.value = e.target.value)
                        }),
                        err.value
                            ? h('p', { style: 'margin:6px 0 0; color:#d32f2f; font-size:12px;' }, err.value)
                            : null
                    ])
            }
        }))

        const ok = await ctx.confirm({
            title: restOptions.title ?? confirmTitle(type),
            confirmText: restOptions.confirmText ?? '확인',
            cancelText: restOptions.cancelText ?? '취소',
            variant: restOptions.variant ?? (type === 'delete' ? 'danger' : 'primary'),
            teleportTo: restOptions.teleportTo ?? 'body',

            content: ContentComp,
            beforeConfirm: () => validate()
        })

        if (!ok) return { ok: false, value: '' }
        return { ok: true, value: val.value.trim() }
    }

    // 단일 input 포함 confirm
    const confirmWithInput = async (type, target = '항목', options = {}) => {
        const { content: contentText, ...restOptions } = options

        const inputName = restOptions.inputName ?? 'value'
        const placeholder = restOptions.placeholder ?? '사유를 입력하세요.'
        const required = restOptions.required ?? true
        const maxLength = restOptions.maxLength ?? 200
        const initialValue = restOptions.initialValue ?? ''

        const val = ref(initialValue)
        const err = ref('')

        const validate = () => {
            const v = (val.value ?? '').trim()
            if (required && !v) {
                err.value = '값을 입력해주세요.'
                return false
            }
            if (maxLength && v.length > maxLength) {
                err.value = `값은 ${maxLength}자 이내로 입력해주세요.`
                return false
            }
            err.value = ''
            return true
        }

        watch(val, () => {
            if (err.value) validate()
        })

        const ContentComp = markRaw(defineComponent({
            name: 'ConfirmInputContent',
            setup() {
                return () =>
                    h('div', { class: 'alert-input-wrap' }, [
                        h('p', { style: 'margin:0 0 8px 0;' }, contentText ?? confirmContent(type, target)),
                        h('input', {
                            id: inputName,
                            name: inputName,
                            maxlength: maxLength,
                            placeholder,
                            value: val.value,
                            style: 'width:100%; box-sizing:border-box; padding:8px; border:1px solid #d0d0d0; border-radius:6px;',
                            onInput: (e) => (val.value = e.target.value)
                        }),
                        err.value
                            ? h('p', { style: 'margin:6px 0 0; color:#d32f2f; font-size:12px;' }, err.value)
                            : null
                    ])
            }
        }))

        const ok = await ctx.confirm({
            title: restOptions.title ?? confirmTitle(type),
            confirmText: restOptions.confirmText ?? '확인',
            cancelText: restOptions.cancelText ?? '취소',
            variant: restOptions.variant ?? (type === 'delete' ? 'danger' : 'primary'),
            teleportTo: restOptions.teleportTo ?? 'body',

            content: ContentComp,
            beforeConfirm: () => validate()
        })

        if (!ok) return { ok: false, value: '' }
        return { ok: true, value: val.value.trim() }
    }

    // 성공
    const success = (options = {}) => {
        const type = typeof options === 'string' ? options : (options.type ?? 'save')
        const title = (typeof options === 'object' ? options.title : null) ?? successTitle(type)
        const message = (typeof options === 'object' ? (options.content ?? options.message) : null) ?? successMessage(type)
        return ctx.success({
            title,
            message
        })
    }

    // 오류
    const error = (options = {}) => {
        const message = typeof options === 'string' ? options : options.message ?? options.content ?? '처리 중 오류가 발생했습니다.'
        const title = (typeof options === 'object' ? options.title : null) ?? '오류'
        return ctx.error({
            title,
            message,
            ...((typeof options === 'object') ? { teleportTo: options.teleportTo } : {})
        })
    }

    // 알림(안내)
    const info = (options = {}) => {
        const message = typeof options === 'string' ? options : options.message ?? options.content ?? ''
        const title = (typeof options === 'object' ? options.title : null) ?? '안내'
        return ctx.info({
            title,
            message,
            ...((typeof options === 'object') ? { teleportTo: options.teleportTo } : {})
        })
    }

    return {
        confirm,
        confirmWithTextarea,
        success,
        error,
        info,
        confirmWithInput,
        warning: ctx.warning,
        isBusy: ctx.isBusy
    }
}
